import { useEffect } from "react";
import { IMAGES } from "@/lib/site";
import { FadeUp, MaskedLine, GoldDot } from "@/components/Reveal";
import Booking from "@/components/Booking";
import Footer from "@/components/Footer";

const CHAPTERS = [
  {
    title: "Science & Healthcare First",
    text: "My professional path began in science. I graduated from the Chemical and Bioscience Technology program and worked in a laboratory before moving into hospital healthcare, where I have spent ten years as a Surgical Processor. My background in laboratory science and hospital healthcare shaped the way I work — with precision, patience, careful observation, and an uncompromising approach to sterility.",
  },
  {
    title: "Into Massage",
    text: "I graduated from Makami College and began my practice as a Registered Massage Therapist. Massage united everything my previous careers taught me: the analytical mind of the laboratory and the calm, careful hands of hospital work.",
  },
  {
    title: "Esthetics",
    text: "I later expanded my training at the European Institute of Esthetics in Edmonton, adding specialized facial skin care knowledge and techniques — so the face receives the same professional attention as the body.",
  },
  {
    title: "Always Learning",
    text: "Continuing education is an ongoing part of my practice: myofascial concepts influenced by Tom Myers’ Anatomy Trains, European massage techniques, and traditional Indonesian approaches, including Balinese massage. I draw from this broad range of knowledge and techniques to support each client’s goals.",
  },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "About Elena — Royal Essence Massage Edmonton";
    return () => {
      document.title = "Royal Essence Massage — Massage Beyond Muscles";
    };
  }, []);

  return (
    <div className="bg-ivory font-sans text-walnut" data-testid="about-page">
      <main>
        <section className="bg-forest pb-24 pt-40 text-ivory lg:pb-32 lg:pt-48">
          <div className="mx-auto grid max-w-[1500px] items-center gap-16 px-6 lg:grid-cols-12 lg:px-14">
            <div className="lg:col-span-7">
              <h1 className="font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl" data-testid="about-headline">
                <MaskedLine delay={0.15}>The hands you can</MaskedLine>
                <MaskedLine delay={0.3}>
                  <span className="italic text-sand">trust with your body</span>
                  <span className="mb-2 ml-3 inline-block h-2 w-2 rounded-full bg-gold align-baseline" />
                </MaskedLine>
              </h1>
              <FadeUp delay={0.45}>
                <p className="mt-10 max-w-xl font-sans text-base font-light leading-relaxed tracking-wide text-ivory/75" data-testid="about-intro">
                  I’m Elena — a Registered Massage Therapist and the founder of
                  Royal Essence Massage. My approach combines therapeutic
                  expertise, attentive care, and a strong foundation in
                  science and healthcare.
                </p>
                <p className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 font-sans text-[10px] font-medium uppercase tracking-[0.3em] text-gold" data-testid="about-journey">
                  Science <GoldDot className="opacity-60" /> Healthcare
                  <GoldDot className="opacity-60" /> Massage
                  <GoldDot className="opacity-60" /> Esthetics
                </p>
              </FadeUp>
            </div>
            <FadeUp className="relative lg:col-span-4 lg:col-start-9" delay={0.3}>
              <div className="absolute -left-4 -top-4 h-full w-full rounded-t-full border border-gold/40" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-t-full border-[10px] border-sand/60">
                <img
                  src={IMAGES.therapist}
                  alt="Elena — Registered Massage Therapist, founder of Royal Essence Massage"
                  className="w-full object-cover"
                  data-testid="about-photo"
                />
              </div>
            </FadeUp>
          </div>
        </section>

        <section className="py-24 lg:py-32" data-chapter="A1" data-testid="about-chapters">
          <div className="mx-auto max-w-[1500px] px-6 lg:px-14">
            <div className="border-t border-walnut/30">
              {CHAPTERS.map((c, i) => (
                <FadeUp key={c.title} delay={Math.min(i * 0.08, 0.3)}>
                  <div className="grid gap-4 border-b border-walnut/30 py-12 lg:grid-cols-12 lg:gap-10" data-testid={`about-chapter-${i}`}>
                    <span className="flex items-center gap-4 lg:col-span-1">
                      <GoldDot />
                      <span className="font-serif text-lg font-light italic text-walnut/65">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </span>
                    <h2 className="font-serif text-3xl font-light tracking-wide text-forest lg:col-span-4 lg:text-4xl">
                      {c.title}
                    </h2>
                    <p className="font-sans text-base font-light leading-[1.9] tracking-wide text-walnut/85 lg:col-span-6 lg:col-start-7">
                      {c.text}
                    </p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-sand/40 py-24 lg:py-32" data-chapter="A2" data-testid="about-philosophy">
          <div className="mx-auto flex max-w-3xl flex-col items-center px-6 text-center">
            <FadeUp>
              <span className="mb-10 inline-block h-[7px] w-[7px] rounded-full bg-accent" aria-hidden="true" />
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="font-serif text-4xl font-light leading-[1.1] tracking-tight text-forest sm:text-5xl" data-testid="about-philosophy-title">
                My Philosophy — <span className="italic text-walnut">Massage Beyond Muscles.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-10 font-sans text-base font-light leading-[1.9] tracking-wide text-walnut/85" data-testid="about-philosophy-text">
                Massage is about more than working with muscles. It is an
                opportunity to help the body slow down, release physical
                tension, and feel more at ease — while giving the mind space
                to step away from everyday demands. It is time to rest,
                reconnect with yourself, and simply be.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <p className="mt-10 font-serif text-2xl font-light italic leading-snug text-forest/90" data-testid="about-quote">
                “I don’t work in haste. I work with listening — and the
                difference is everything.”
              </p>
              <p className="mt-6 font-sans text-[11px] font-medium uppercase tracking-[0.35em] text-walnut/75">
                Elena — Founder, Royal Essence Massage, RMT
              </p>
            </FadeUp>
          </div>
        </section>

        <Booking />
      </main>
      <Footer />
    </div>
  );
}
